import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Eye } from 'lucide-react'
import { toast } from 'sonner'
import { apiGet, apiPost } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { UserAvatar } from '@/components/composites/user-avatar'
import { setViewAs, type ViewingAs } from '@/lib/view-as'

interface Candidate extends ViewingAs { roleLabel: string }

/** Pick somebody to see Tapwork as. Only people whose access is no wider than yours are offered. */
export function ViewAsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [needle, setNeedle] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const query = useQuery({ queryKey: ['view-as', 'candidates'], queryFn: () => apiGet<{ items: Candidate[] }>('members/view-as/candidates'), enabled: open, staleTime: 60_000 })
  const shown = useMemo(() => {
    const n = needle.trim().toLowerCase()
    return (query.data?.items ?? []).filter((c) => !n || `${c.displayName} ${c.email}`.toLowerCase().includes(n))
  }, [query.data, needle])

  const start = async (person: Candidate) => {
    setBusy(person.id)
    try {
      const started = await apiPost<ViewingAs>('members/view-as/start', { userId: person.id })
      setViewAs({ id: started.id, displayName: started.displayName, email: started.email, photoUrl: started.photoUrl })
      // A reload, not a cache swap: nothing fetched as you may be shown as them.
      window.location.assign('/')
    } catch (error) {
      toast.error(getErrorMessage(error))
      setBusy(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eye className="size-5 text-brand" />View as…</DialogTitle>
          <DialogDescription>See Tapwork exactly as they do. It is read-only, their calendar and mailbox are never shown, and it is recorded in the audit log.</DialogDescription>
        </DialogHeader>
        <Input autoFocus value={needle} onChange={(e) => setNeedle(e.target.value)} placeholder="Search people" aria-label="Search people" />
        <ul className="max-h-80 overflow-y-auto">
          {query.isLoading ? <li><Skeleton className="h-10" /></li> : null}
          {query.isError ? <li className="py-3 text-sm text-danger">{getErrorMessage(query.error)}</li> : null}
          {!query.isLoading && !query.isError && shown.length === 0 ? <li className="py-6 text-center text-sm text-muted-foreground">Nobody to view. You can only view people whose access is no wider than yours.</li> : null}
          {shown.map((c) => (
            <li key={c.id}>
              <button type="button" disabled={busy !== null} onClick={() => void start(c)} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-accent disabled:opacity-60">
                <UserAvatar person={{ id: c.id, displayName: c.displayName, photoUrl: c.photoUrl }} size="md" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{c.displayName}</span>
                  <span className="block truncate text-xs text-muted-foreground">{c.email}</span>
                </span>
                <span className="text-xs capitalize text-muted-foreground">{busy === c.id ? 'Opening…' : c.roleLabel}</span>
              </button>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  )
}
